import type { AxiosPromise } from 'axios';
import BaseClient, { type ExtendedAxiosRequestConfig } from './BaseClient';
import type { CursorPaginationResponse, QueryParams } from './api';
import type {
    CreateTeamData,
    EmailDomainInvite,
    GetTeamsParams,
    KioskTeam,
    KioskTeamListItem,
    SetPricingData,
    TeamPricingItem,
    ThroughputConfig,
    UpdateBillingOptionsData,
} from './adminTeamsClient.types';

class AdminTeamsClient extends BaseClient {
    getTeams(
        params: GetTeamsParams,
        config?: ExtendedAxiosRequestConfig
    ): AxiosPromise<CursorPaginationResponse<KioskTeamListItem>> {
        const url = `/api/admin/teams`;
        return this.makeCoreApiRequest(url, 'get', null, params, config);
    }

    getSubTeams(teamId: number, params?: QueryParams): AxiosPromise<CursorPaginationResponse<KioskTeamListItem>> {
        const url = `/api/admin/teams/${teamId}/sub-teams`;
        return this.makeCoreApiRequest(url, 'get', null, params);
    }

    countSubTeams(teamId: number, params?: QueryParams): AxiosPromise<{ count: number }> {
        const url = `/api/admin/teams/${teamId}/sub-teams/count`;
        return this.makeCoreApiRequest(url, 'get', null, params);
    }

    suspendSubTeams(teamId: number, data: { reason?: string | null }): AxiosPromise<{ count: number }> {
        const url = `/api/admin/teams/${teamId}/sub-teams/suspend`;
        return this.makeCoreApiRequest(url, 'post', data);
    }

    getTeam(teamId: number): AxiosPromise<KioskTeam> {
        const url = `/api/admin/teams/${teamId}`;
        return this.makeCoreApiRequest(url, 'get');
    }

    favoriteTeam(teamId: number): AxiosPromise<void> {
        const url = `/api/admin/teams/${teamId}/favorite`;
        return this.makeCoreApiRequest(url, 'post');
    }

    deleteFavoriteTeam(teamId: number): AxiosPromise<void> {
        const url = `/api/admin/teams/${teamId}/favorite`;
        return this.makeCoreApiRequest(url, 'delete');
    }

    activateTeam(teamId: number): AxiosPromise<void> {
        const url = `/api/admin/teams/${teamId}/activate`;
        return this.makeCoreApiRequest(url, 'post');
    }

    suspendTeam(teamId: number, data: { reason?: string | null }): AxiosPromise<KioskTeam> {
        const url = `/api/admin/teams/${teamId}/suspend`;
        return this.makeCoreApiRequest(url, 'post', data);
    }

    createTeam(data: CreateTeamData): AxiosPromise<{ id: number }> {
        const url = `/api/admin/teams`;
        return this.makeCoreApiRequest(url, 'post', data);
    }

    updateBillingOptions(teamId: number, data: UpdateBillingOptionsData): AxiosPromise<void> {
        const url = `/api/admin/teams/${teamId}/billing-options`;
        return this.makeCoreApiRequest(url, 'put', data);
    }

    unsetBillingInformation(teamId: number): AxiosPromise<void> {
        const url = `/api/admin/teams/${teamId}/billing-information`;
        return this.makeCoreApiRequest(url, 'delete');
    }

    unsetBillingEmail(teamId: number): AxiosPromise<void> {
        const url = `/api/admin/teams/${teamId}/billing-email`;
        return this.makeCoreApiRequest(url, 'delete');
    }

    getPricing(teamId: number): AxiosPromise<{ items: TeamPricingItem[] }> {
        const url = `/api/admin/teams/${teamId}/pricing`;
        return this.makeCoreApiRequest(url, 'get');
    }

    setPricing(teamId: number, data: SetPricingData): AxiosPromise<void> {
        const url = `/api/admin/teams/${teamId}/pricing`;
        return this.makeCoreApiRequest(url, 'put', data);
    }

    getHubspotCompanyId(teamId: number): AxiosPromise<{ hubspot_company_id: string | null }> {
        const url = `/api/admin/teams/${teamId}/hubspot-company-id`;
        return this.makeCoreApiRequest(url, 'get');
    }

    updateHubspotCompanyId(teamId: number, data: { hubspot_company_id: string | null }): AxiosPromise<void> {
        const url = `/api/admin/teams/${teamId}/hubspot-company-id`;
        return this.makeCoreApiRequest(url, 'patch', data);
    }

    getEmailDomainInvites(teamId: number): AxiosPromise<{ data: EmailDomainInvite[] }> {
        const url = `/api/admin/teams/${teamId}/email-domain-invites`;
        return this.makeCoreApiRequest(url, 'get');
    }

    createEmailDomainInvite(teamId: number, data: { domain: string }): AxiosPromise<EmailDomainInvite> {
        const url = `/api/admin/teams/${teamId}/email-domain-invites`;
        return this.makeCoreApiRequest(url, 'post', data);
    }

    deleteEmailDomainInvite(teamId: number, emailDomainInviteId: number): AxiosPromise<void> {
        const url = `/api/admin/teams/${teamId}/email-domain-invites/${emailDomainInviteId}`;
        return this.makeCoreApiRequest(url, 'delete');
    }

    updateCustomAnalytics(teamId: number, data: { dashboard_url: string | null }): AxiosPromise<void> {
        const url = `/api/admin/teams/${teamId}/custom-analytics`;
        return this.makeCoreApiRequest(url, 'put', data);
    }

    getDefaultGroupStatus(teamId: number): AxiosPromise<{ default_group_status: 'draft' | 'active' }> {
        const url = `/api/admin/teams/${teamId}/default-group-status`;
        return this.makeCoreApiRequest(url, 'get');
    }

    updateDefaultGroupStatus(teamId: number, data: { default_group_status: 'draft' | 'active' }): AxiosPromise<void> {
        const url = `/api/admin/teams/${teamId}/default-group-status`;
        return this.makeCoreApiRequest(url, 'put', data);
    }

    getThroughputs(teamId: number): AxiosPromise<{ data: ThroughputConfig[] }> {
        const url = `/api/admin/teams/${teamId}/throughputs`;
        return this.makeCoreApiRequest(url, 'get');
    }

    updateThroughput(teamId: number, type: string, rate: number): AxiosPromise<void> {
        const url = `/api/admin/teams/${teamId}/throughputs/${type}`;
        return this.makeCoreApiRequest(url, 'put', { throughput_rate: rate });
    }
}

export default new AdminTeamsClient();
